import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardHeader, CardBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import "moment/locale/pt-br";

import api from "../../services/api";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "./styles";

const POLL_INTERVAL = 3 * 60 * 1000; // 3 minutos
const TIMESTAMP_INTERVAL = 30 * 1000; // atualiza texto "há X min" a cada 30s

const FORMAT = {
  dolar:      (v) => `R$ ${Number(v).toFixed(2)}`,
  euro:       (v) => `R$ ${Number(v).toFixed(2)}`,
  ibovespa:   (v) => Number(v).toLocaleString("pt-BR"),
  diesel_s10: (v) => `R$ ${Number(v).toFixed(2)}/L`,
};

const CHANGE_FORMAT = {
  dolar:      (c) => (c >= 0 ? `+R$ ${c.toFixed(4)}` : `-R$ ${Math.abs(c).toFixed(4)}`),
  euro:       (c) => (c >= 0 ? `+R$ ${c.toFixed(4)}` : `-R$ ${Math.abs(c).toFixed(4)}`),
  ibovespa:   (c) => (c >= 0 ? `+${c.toLocaleString("pt-BR")}` : `${c.toLocaleString("pt-BR")}`),
  diesel_s10: (c) => (c >= 0 ? `+R$ ${c.toFixed(4)}/L` : `-R$ ${Math.abs(c).toFixed(4)}/L`),
};

const ICON = {
  dolar:      "$",
  euro:       "€",
  ibovespa:   "B3",
  diesel_s10: "⛽",
};

const COLOR_UP     = "var(--everlog-status-success)";
const COLOR_DOWN   = "var(--everlog-status-danger)";
const COLOR_STABLE = "var(--everlog-status-neutral)";

function directionColor(d) {
  if (d === "up")   return COLOR_UP;
  if (d === "down") return COLOR_DOWN;
  return COLOR_STABLE;
}

function directionArrow(d) {
  if (d === "up")   return "▲";
  if (d === "down") return "▼";
  return "—";
}

function relativeTime(ts) {
  if (!ts) return "";
  const diff = moment().diff(moment(ts), "minutes");
  if (diff < 1)  return "agora";
  if (diff === 1) return "há 1 min";
  if (diff < 60) return `há ${diff} min`;
  const hours = Math.floor(diff / 60);
  if (hours === 1) return "há 1 hora";
  return `há ${hours} horas`;
}

export default function MarketIndicatorsCard() {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [, forceRefreshTs]          = useState(0);
  const [tooltip, setTooltip]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRef = useRef(null);

  const fetchIndicators = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setRefreshing(true);
      const response = await api.get("/market/indicators");
      if (response.status === 200) {
        setIndicators(response.data.indicators || []);
        setGeneratedAt(response.data.generatedAt);
        setError(null);
      }
    } catch (err) {
      console.error("[MarketIndicatorsCard] fetch error", err);
      setError("Não foi possível carregar os indicadores.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Busca inicial
  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  // Polling a cada 3 minutos, somente com aba visível
  useEffect(() => {
    fetchRef.current = fetchIndicators;
  }, [fetchIndicators]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchRef.current(true);
      }
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Atualiza timestamp relativo a cada 30s sem re-fetch
  useEffect(() => {
    const interval = setInterval(() => forceRefreshTs(Date.now()), TIMESTAMP_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const textColor = AUTOPILOT_COLORS.title;
  const subColor = AUTOPILOT_COLORS.muted;
  const cardBg = AUTOPILOT_COLORS.insetSurface;
  const borderColor = AUTOPILOT_COLORS.border;

  return (
    <Card style={{ ...autopilotCardStyle, display: "flex", flexDirection: "column", flex: 1, background: "var(--everlog-card-hero-bg)" }}>
      <CardHeader style={autopilotHeaderStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={autopilotEyebrowStyle}>Contexto externo</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.3rem" }}>Mercado</div>
            {generatedAt && (
              <small style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.72rem" }}>
                Atualizado {relativeTime(generatedAt)}
              </small>
            )}
          </div>
          {refreshing && (
            <ClipLoader sizeUnit="px" size={14} color="var(--theme-accent)" loading />
          )}
        </div>
      </CardHeader>

      <CardBody style={{ ...autopilotBodyStyle, flex: 1 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <ClipLoader sizeUnit="px" size={40} color="var(--theme-accent)" loading />
          </div>
        ) : error ? (
          <p style={{ color: COLOR_DOWN, fontSize: "0.8rem", textAlign: "center" }}>{error}</p>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              {indicators.map((ind) => {
                if (!ind) return null;
                const fmt   = FORMAT[ind.key]   || ((v) => v);
                const color = directionColor(ind.direction);

                return (
                  <div
                    key={ind.key}
                    style={{
                      background: cardBg,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 14,
                      padding: "10px 12px",
                      cursor: "pointer",
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        x: rect.left + rect.width / 2,
                        y: rect.top - 8,
                        ind,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "0.68rem", color: subColor, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {ind.label}
                      </span>
                      <span style={{ fontSize: "0.75rem", color: subColor, fontWeight: 600 }}>
                        {ICON[ind.key]}
                      </span>
                    </div>
                    <div style={{ marginTop: 4, fontSize: "1.05rem", fontWeight: 700, color: textColor }}>
                      {fmt(ind.value)}
                    </div>
                    <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ color, fontSize: "0.72rem", fontWeight: 600 }}>
                        {directionArrow(ind.direction)}
                      </span>
                      <span style={{ color, fontSize: "0.72rem" }}>
                        {ind.changePct != null
                          ? `${(ind.changePct * 100).toFixed(2)}%`
                          : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

          </>
        )}
      </CardBody>

      {/* Tooltip */}
      {tooltip && tooltip.ind && (() => {
        const ind = tooltip.ind;
        const fmt    = FORMAT[ind.key]       || ((v) => v);
        const chgFmt = CHANGE_FORMAT[ind.key] || ((c) => c);
        const color  = directionColor(ind.direction);
        return (
          <div
            style={{
              position: "fixed",
              left: tooltip.x,
              top: tooltip.y,
              transform: "translate(-50%, -100%)",
              background: AUTOPILOT_COLORS.tooltipBg,
              color: AUTOPILOT_COLORS.tooltipText,
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: "0.78rem",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              zIndex: 9999,
              border: `1px solid ${AUTOPILOT_COLORS.tooltipBorder}`,
              boxShadow: AUTOPILOT_COLORS.tooltipShadow,
              lineHeight: 1.6,
            }}
          >
            <strong>{ind.label}</strong>
            <br />
            {fmt(ind.value)}
            <br />
            <span style={{ color }}>{chgFmt(ind.change || 0)}</span>
            <br />
            <span style={{ opacity: 0.7, fontSize: "0.7rem" }}>
              {ind.updatedAt
                ? moment(ind.updatedAt).format("HH:mm [de hoje]")
                : ""}
            </span>
            <br />
            <span style={{ opacity: 0.6, fontSize: "0.68rem" }}>Fonte: {ind.source}</span>
          </div>
        );
      })()}
    </Card>
  );
}
